import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import { useActiveModuleTemplateListQuery } from "entities/modules/queries/useActiveModuleTemplateList";
import { ModuleData } from "entities/modules/types";
import { useCreateModuleBatchMutation } from "entities/pages/mutations/useCreateModuleBatchMutation";
import { invalidateModuleListByBrandPageQuery } from "entities/pages/queries/useModuleListByBrandPageQuery";
import { PageModuleDataToInsert, PageModuleStatus } from "entities/pages/types";
import React, { FC, SyntheticEvent, useState } from "react";
import AutocompleteField from "shared/components/Form/AutocompleteField";
import TextField from "shared/components/Form/TextField";
import Modal from "shared/components/Modal";
import { COLOR_BLACK } from "shared/theme/colors";
import { CheckBoxIcon } from "shared/theme/icons/CheckBoxIcon";
import { CheckedBoxIcon } from "shared/theme/icons/CheckedBoxIcon";
import { usePageModules } from "../../context";
import { AutocompleteSkeleton } from "./skeleton";

type Props = {
  open: boolean;
  onClose: () => void;
};

const PasteModulePopup: FC<Props> = (props) => {
  const [selectedModules, setSelectedModules] = useState<ModuleData[]>([]);

  const { brandId, pageId, isLoadingPages } = usePageModules();

  const { data = [], isLoading } = useActiveModuleTemplateListQuery(brandId);

  const { mutate, isPending } = useCreateModuleBatchMutation();

  const handleModulesChange = (_: SyntheticEvent, value: ModuleData[]) => {
    setSelectedModules(value);
  };

  const handleSubmitSuccess = () => {
    props.onClose();
    invalidateModuleListByBrandPageQuery(brandId, pageId);
    setSelectedModules([]);
  };

  const handleSubmit = () => {
    const formattedBatchData: PageModuleDataToInsert[] = selectedModules.map(
      (module, idx) => ({
        ...module,
        status: PageModuleStatus.Draft,
        order: idx + 1,
      }),
    );

    mutate({
      brandId,
      pageId,
      batchData: formattedBatchData,
      onSuccess: handleSubmitSuccess,
    });
  };

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title="Insert Modules"
      secondaryButtonTitle="Cancel"
      onSecondaryClick={props.onClose}
      primaryButtonTitle="Add"
      primaryColor="primary"
      onPrimaryClick={handleSubmit}
      isSubmitting={isPending}
      isDisabled={isLoadingPages}
    >
      {isLoading && <AutocompleteSkeleton />}
      {!isLoading && (
        <AutocompleteField
          multiple
          disableCloseOnSelect
          id="pasteModulesAutocomplete"
          value={selectedModules}
          options={data}
          onChange={handleModulesChange}
          getOptionLabel={(option) => option.name}
          getOptionKey={(option) => option.id}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField {...params} placeholder="Add a module" />
          )}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={props.key}>
              <Checkbox
                icon={<CheckBoxIcon />}
                checkedIcon={<CheckedBoxIcon />}
                style={{ marginRight: 8 }}
                checked={selected}
                id={props.id}
              />
              <Typography color={COLOR_BLACK} variant="bodyMRegular">
                {option.name}
              </Typography>
            </li>
          )}
        />
      )}
    </Modal>
  );
};

export default PasteModulePopup;
